import React from "react";
import {
  format as formatDate,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  parseISO,
  isToday,
  addWeeks,
  addMonths,
  addDays,
  isSameDay,
  isWithinInterval,
  isBefore,
  startOfToday,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Clock,
  Plus,
} from "lucide-react";
import { OrganizationJobOffer } from "./organization-job-offer-calendar";
import { JobOfferPreviewDialog } from "./job-offer-preview-dialog";
import { getJobOfferOrgDisplayName } from "@/features/job-offer/utils/get-job-offer-org-display";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

type SchedulerView = "week" | "day" | "month";

type GridShift = {
  id: string;
  jobOfferId: string;
  title: string;
  providerDisplayName: string;
  facilityName: string | null;
  startsAt: Date;
  endsAt: Date;
  dayKey: string; // "YYYY-MM-DD" format for grouping
  status: "scheduled" | "pending";
  timeRange: string;
  jobOffer: OrganizationJobOffer;
};

type ProviderScheduleRow = {
  providerId: string;
  providerName: string;
  specialty?: string | null;
  acceptedAt: string | null;
  shifts: GridShift[];
};

type WeeklyGridData = {
  rangeStart: Date;
  rangeEnd: Date;
  days: Date[];
  openShifts: GridShift[];
  providerRows: ProviderScheduleRow[];
};

type OrganizationJobOfferWeeklySchedulerProps = {
  jobOffers?: OrganizationJobOffer[];
  date: Date;
  onNavigate: (date: Date) => void;
  onCreateFromSelection?: (params: {
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
  }) => void;
  onRequestEdit?: (jobOfferId: string) => void;
  onCanceled?: () => void;
  user: { is_admin: boolean } | null | undefined;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format a time range from Date objects to compact string format.
 * Example: "8:00a - 8:00p" or "11:30p - 7:30a"
 */
function formatTimeRange(start: Date, end: Date): string {
  const formatTime = (d: Date) => {
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const period = hours >= 12 ? "p" : "a";
    const displayHours = hours % 12 || 12;
    const displayMinutes =
      minutes === 0 ? "" : `:${minutes.toString().padStart(2, "0")}`;
    return `${displayHours}${displayMinutes}${period}`;
  };

  return `${formatTime(start)} - ${formatTime(end)}`;
}

/**
 * Determine if a job offer has been accepted by a provider.
 */
function getAcceptedDecision(jobOffer: OrganizationJobOffer) {
  return jobOffer.decisions?.find(
    (d) => d.providerDecision === true && !d.providerCanceledAt,
  );
}

/**
 * Transform job offers into grid data structure for the scheduler.
 */
function transformToGridData(
  jobOffers: OrganizationJobOffer[] | undefined,
  rangeStart: Date,
  rangeEnd: Date,
  days: Date[],
): WeeklyGridData {
  const openShifts: GridShift[] = [];
  const providerMap = new Map<string, ProviderScheduleRow>();

  if (!jobOffers) {
    return { rangeStart, rangeEnd, days, openShifts, providerRows: [] };
  }

  jobOffers.forEach((offer) => {
    // Skip canceled offers
    if (offer.canceledByOrganizationBusinessUnitAt) {
      return;
    }

    const acceptedDecision = getAcceptedDecision(offer);
    const offerShiftStatus: GridShift["status"] = acceptedDecision
      ? "scheduled"
      : "pending";

    const candidateShifts: Array<{
      id: string;
      startsAt: Date | string;
      endsAt: Date | string;
      status: GridShift["status"];
    }> = [
      ...(offer.shifts ?? [])
        .filter((shift) => shift.status !== "canceled")
        .map((shift) => ({ ...shift, status: offerShiftStatus })),
      // Dates a pending proposal would add are not shifts until the proposal
      // is accepted, so show them as pending in the offer's row.
      ...(offer.pendingProposalShifts ?? []).map((proposedShift) => ({
        id: `${proposedShift.proposalId}-${String(proposedShift.startsAt)}`,
        startsAt: proposedShift.startsAt,
        endsAt: proposedShift.endsAt,
        status: "pending" as const,
      })),
    ];

    candidateShifts.forEach((shift) => {
      const shiftStart =
        typeof shift.startsAt === "string"
          ? parseISO(shift.startsAt)
          : shift.startsAt;
      const shiftEnd =
        typeof shift.endsAt === "string"
          ? parseISO(shift.endsAt)
          : shift.endsAt;

      // Filter to current range only
      if (
        !isWithinInterval(shiftStart, { start: rangeStart, end: rangeEnd }) &&
        !isWithinInterval(shiftEnd, { start: rangeStart, end: rangeEnd })
      ) {
        return;
      }

      const displayInfo = getJobOfferOrgDisplayName(offer);
      const gridShift: GridShift = {
        id: shift.id,
        jobOfferId: offer.id,
        title: offer.title || "",
        providerDisplayName: displayInfo.primaryName,
        facilityName: offer.facility?.name || null,
        startsAt: shiftStart,
        endsAt: shiftEnd,
        dayKey: formatDate(shiftStart, "yyyy-MM-dd"),
        status: shift.status,
        timeRange: formatTimeRange(shiftStart, shiftEnd),
        jobOffer: offer,
      };

      if (acceptedDecision) {
        // Add to provider row
        const providerId = acceptedDecision.userId;
        if (!providerMap.has(providerId)) {
          const providerName =
            `${acceptedDecision.firstName ?? ""} ${acceptedDecision.lastName ?? ""}`
              .trim()
              .replace(/\s+/g, " ") || "Unnamed Provider";

          providerMap.set(providerId, {
            providerId,
            providerName,
            acceptedAt: acceptedDecision.providerDecisionAt ?? null,
            shifts: [],
          });
        }
        const provider = providerMap.get(providerId);
        if (provider) {
          provider.shifts.push(gridShift);
        }
      } else {
        // Add to open shifts
        openShifts.push(gridShift);
      }
    });
  });

  // Sort provider rows by name
  const providerRows = Array.from(providerMap.values()).sort((a, b) =>
    a.providerName.localeCompare(b.providerName),
  );

  return { rangeStart, rangeEnd, days, openShifts, providerRows };
}

// ============================================================================
// Sub-Components
// ============================================================================

const statusConfig = {
  scheduled: {
    badge: "bg-emerald-100 text-emerald-700",
    card: "bg-emerald-50 border-emerald-400 hover:border-emerald-500",
    dot: "bg-emerald-500",
  },
  pending: {
    badge: "bg-primary/10 text-primary",
    card: "bg-primary/5 border-primary/50 border-dashed hover:border-primary",
    dot: "border-2 border-dashed border-primary bg-primary/10",
  },
};

type ShiftCardProps = {
  shift: GridShift;
  onClick: () => void;
  compact?: boolean;
};

const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onClick,
  compact = false,
}) => {
  const config = statusConfig[shift.status];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-lg border-2 p-2 transition-all hover:shadow-sm cursor-pointer",
        config.card,
        compact && "p-1.5",
      )}
    >
      <div className="flex items-start gap-1.5">
        <span
          className="text-xs font-semibold text-slate-800 truncate flex-1"
          title={shift.providerDisplayName}
        >
          {shift.providerDisplayName}
        </span>
      </div>
      <div
        className={cn(
          "mt-1.5 flex items-center gap-1 text-xs text-slate-600",
          compact && "mt-1",
        )}
      >
        <Clock className="h-3 w-3 shrink-0" />
        <span className="font-medium">{shift.timeRange}</span>
      </div>
      {!compact && shift.title && (
        <p className="mt-1 text-xs text-slate-500 truncate" title={shift.title}>
          {shift.title}
        </p>
      )}
    </button>
  );
};

type DayCellProps = {
  day: Date;
  shifts: GridShift[];
  onShiftClick: (shift: GridShift) => void;
  onCellClick: (day: Date) => void;
  isHeader?: boolean;
  showAddButton?: boolean;
};

const DayCell: React.FC<DayCellProps> = ({
  day,
  shifts,
  onShiftClick,
  onCellClick,
  showAddButton = true,
}) => {
  const today = isToday(day);
  const isPast = isBefore(day, startOfToday());

  return (
    <div
      className={cn(
        "min-h-[100px] border-l border-slate-200 p-2 relative group",
        today && "bg-primary/5",
        !isPast && "cursor-pointer hover:bg-slate-50",
      )}
      onClick={() => !isPast && shifts.length === 0 && onCellClick(day)}
    >
      <div className="space-y-1.5">
        {shifts.map((shift) => (
          <ShiftCard
            key={shift.id}
            shift={shift}
            onClick={() => onShiftClick(shift)}
            compact={shifts.length > 2}
          />
        ))}
      </div>
      {showAddButton && !isPast && shifts.length === 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCellClick(day);
          }}
          className="absolute inset-2 hidden group-hover:flex items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
        >
          <Plus className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const OrganizationJobOfferWeeklyScheduler: React.FC<
  OrganizationJobOfferWeeklySchedulerProps
> = ({
  jobOffers,
  date,
  onNavigate,
  onCreateFromSelection,
  onRequestEdit,
  onCanceled,
  user,
}) => {
  const viewControlId = React.useId();
  const [selectedJobOffer, setSelectedJobOffer] =
    React.useState<OrganizationJobOffer | null>(null);
  const [view, setView] = React.useState<SchedulerView>("week");

  // Calculate date range based on view
  const { rangeStart, rangeEnd, days } = React.useMemo(() => {
    let start: Date;
    let end: Date;

    switch (view) {
      case "day":
        start = startOfDay(date);
        end = endOfDay(date);
        break;
      case "month":
        start = startOfMonth(date);
        end = endOfMonth(date);
        break;
      case "week":
      default:
        start = startOfWeek(date, { weekStartsOn: 0 });
        end = endOfWeek(date, { weekStartsOn: 0 });
        break;
    }

    const daysArray = eachDayOfInterval({ start, end });
    return { rangeStart: start, rangeEnd: end, days: daysArray };
  }, [date, view]);

  // Transform job offers into grid data
  const gridData = React.useMemo(
    () => transformToGridData(jobOffers, rangeStart, rangeEnd, days),
    [jobOffers, rangeStart, rangeEnd, days],
  );

  // Navigation handlers
  const handlePrev = React.useCallback(() => {
    switch (view) {
      case "day":
        onNavigate(addDays(date, -1));
        break;
      case "month":
        onNavigate(addMonths(date, -1));
        break;
      case "week":
      default:
        onNavigate(addWeeks(date, -1));
        break;
    }
  }, [date, onNavigate, view]);

  const handleNext = React.useCallback(() => {
    switch (view) {
      case "day":
        onNavigate(addDays(date, 1));
        break;
      case "month":
        onNavigate(addMonths(date, 1));
        break;
      case "week":
      default:
        onNavigate(addWeeks(date, 1));
        break;
    }
  }, [date, onNavigate, view]);

  const handleToday = React.useCallback(() => {
    onNavigate(new Date());
  }, [onNavigate]);

  // Cell click handler
  const handleCellClick = React.useCallback(
    (day: Date) => {
      if (isBefore(day, startOfToday())) {
        toast("Cannot create offers in the past", {
          description: "Please select today or a future date.",
        });
        return;
      }

      const dateStr = formatDate(day, "yyyy-MM-dd");
      onCreateFromSelection?.({
        startDate: dateStr,
        endDate: dateStr,
      });
    },
    [onCreateFromSelection],
  );

  // Shift click handler
  const handleShiftClick = React.useCallback((shift: GridShift) => {
    setSelectedJobOffer(shift.jobOffer);
  }, []);

  // Get shifts for a specific day
  const getShiftsForDay = React.useCallback(
    (day: Date, shifts: GridShift[]) => {
      return shifts.filter((shift) => isSameDay(shift.startsAt, day));
    },
    [],
  );

  // Date range display
  const dateRangeDisplay = React.useMemo(() => {
    switch (view) {
      case "day":
        return formatDate(date, "EEEE, MMMM d, yyyy");
      case "month":
        return formatDate(date, "MMMM yyyy");
      case "week":
      default:
        return `${formatDate(rangeStart, "MMM d")} - ${formatDate(rangeEnd, "MMM d, yyyy")}`;
    }
  }, [date, rangeStart, rangeEnd, view]);

  // Grid columns based on view
  const gridColumns = React.useMemo(() => {
    if (view === "day") {
      return "240px 1fr";
    }
    if (view === "month") {
      return `240px repeat(7, minmax(100px, 1fr))`;
    }
    return `240px repeat(${days.length}, minmax(120px, 1fr))`;
  }, [view, days.length]);

  const hasData =
    gridData.openShifts.length > 0 || gridData.providerRows.length > 0;

  return (
    <>
      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white shadow-xs overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            {/* Navigation */}
            <div className="flex h-12 shrink-0 items-center rounded-xl border border-slate-200 bg-white p-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={handlePrev}
                className="h-10 w-10 rounded-lg p-0 text-slate-600 hover:bg-slate-100 hover:text-primary"
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleToday}
                className="h-10 rounded-lg px-3 text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-primary"
              >
                Today
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleNext}
                className="h-10 w-10 rounded-lg p-0 text-slate-600 hover:bg-slate-100 hover:text-primary"
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Date Range Display */}
            <span className="text-sm font-semibold text-slate-700">
              {dateRangeDisplay}
            </span>
          </div>

          <div className="flex w-full flex-wrap items-center gap-4 sm:w-auto">
            {/* Status Legend */}
            <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "h-2.5 w-2.5 rounded-full",
                    statusConfig.scheduled.dot,
                  )}
                />
                <span>Scheduled</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "h-2.5 w-2.5 rounded-full",
                    statusConfig.pending.dot,
                  )}
                />
                <span>Pending</span>
              </div>
            </div>

            {/* View Switcher */}
            <fieldset className="grid h-12 w-full grid-cols-3 rounded-xl border border-slate-200 bg-slate-100 sm:w-auto">
              <legend className="sr-only">Scheduler view</legend>
              {(["day", "week", "month"] as SchedulerView[]).map((v) => (
                <label
                  key={v}
                  className="relative flex h-full min-w-0 cursor-pointer select-none p-1"
                >
                  <input
                    type="radio"
                    name={`${viewControlId}-scheduler-view`}
                    value={v}
                    checked={view === v}
                    onChange={() => setView(v)}
                    className="peer sr-only"
                  />
                  <span className="flex min-w-0 flex-1 items-center justify-center rounded-lg px-3 text-sm font-medium capitalize text-slate-600 transition-colors hover:text-slate-900 peer-checked:bg-white peer-checked:text-primary peer-checked:shadow-xs peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-slate-100">
                    {v}
                  </span>
                </label>
              ))}
            </fieldset>
          </div>
        </div>

        {/* Grid */}
        <div className="overflow-x-auto">
          <div className={cn("min-w-[900px]", view === "day" && "min-w-0")}>
            {/* Header */}
            <div
              className="grid border-b border-slate-200 bg-slate-50"
              style={{ gridTemplateColumns: gridColumns }}
            >
              <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {view === "month" ? "Provider / Week" : "Provider"}
              </div>
              {view === "month"
                ? // Month view: show day names only
                  ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div
                        key={day}
                        className="flex items-center justify-center border-l border-slate-200 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500"
                      >
                        {day}
                      </div>
                    ),
                  )
                : // Week/Day view: show day names with dates
                  days.map((day) => (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        "flex flex-col items-center justify-center border-l border-slate-200 py-2",
                        isToday(day) && "bg-primary/5",
                      )}
                    >
                      <span className="text-xs font-medium text-slate-500">
                        {formatDate(day, "EEE")}
                      </span>
                      <span
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold mt-1",
                          isToday(day)
                            ? "bg-primary text-primary-foreground"
                            : "text-slate-700",
                        )}
                      >
                        {formatDate(day, "d")}
                      </span>
                    </div>
                  ))}
            </div>

            {/* Empty State */}
            {!hasData && (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                <CalendarDays className="h-10 w-10 text-slate-300" />
                <div className="text-sm font-medium text-slate-600">
                  No shifts scheduled for this {view}
                </div>
                <div className="text-sm text-slate-500">
                  Click on any cell to create a new job offer, or select a
                  different date range.
                </div>
              </div>
            )}

            {/* Open Shifts Row */}
            {gridData.openShifts.length > 0 && (
              <div
                className="grid border-b border-slate-200"
                style={{ gridTemplateColumns: gridColumns }}
              >
                <div className="flex flex-col gap-1 border-r border-slate-200 bg-amber-50 px-4 py-3">
                  <span className="font-semibold text-amber-800">
                    Open Shifts
                  </span>
                  <span className="text-xs text-amber-600">
                    {gridData.openShifts.length} shift
                    {gridData.openShifts.length !== 1 ? "s" : ""} awaiting
                    response
                  </span>
                </div>
                {view === "month" ? (
                  // Month view: render weeks as rows, days as columns
                  <div className="col-span-7 grid grid-cols-7">
                    {days.map((day) => {
                      const dayShifts = getShiftsForDay(
                        day,
                        gridData.openShifts,
                      );
                      return (
                        <DayCell
                          key={day.toISOString()}
                          day={day}
                          shifts={dayShifts}
                          onShiftClick={handleShiftClick}
                          onCellClick={handleCellClick}
                          showAddButton={false}
                        />
                      );
                    })}
                  </div>
                ) : (
                  days.map((day) => {
                    const dayShifts = getShiftsForDay(day, gridData.openShifts);
                    return (
                      <DayCell
                        key={day.toISOString()}
                        day={day}
                        shifts={dayShifts}
                        onShiftClick={handleShiftClick}
                        onCellClick={handleCellClick}
                        showAddButton={false}
                      />
                    );
                  })
                )}
              </div>
            )}

            {/* Provider Rows */}
            {gridData.providerRows.map((row) => (
              <div
                key={row.providerId}
                className="grid border-b border-slate-200 last:border-b-0"
                style={{ gridTemplateColumns: gridColumns }}
              >
                <div className="flex flex-col gap-1 border-r border-slate-200 bg-white px-4 py-3">
                  <span className="font-medium text-slate-900">
                    {row.providerName}
                  </span>
                  {row.acceptedAt && (
                    <span className="text-xs text-slate-500">
                      Confirmed{" "}
                      {formatDate(parseISO(row.acceptedAt), "MMM d, yyyy")}
                    </span>
                  )}
                </div>
                {view === "month" ? (
                  <div className="col-span-7 grid grid-cols-7">
                    {days.map((day) => {
                      const dayShifts = getShiftsForDay(day, row.shifts);
                      return (
                        <DayCell
                          key={day.toISOString()}
                          day={day}
                          shifts={dayShifts}
                          onShiftClick={handleShiftClick}
                          onCellClick={handleCellClick}
                        />
                      );
                    })}
                  </div>
                ) : (
                  days.map((day) => {
                    const dayShifts = getShiftsForDay(day, row.shifts);
                    return (
                      <DayCell
                        key={day.toISOString()}
                        day={day}
                        shifts={dayShifts}
                        onShiftClick={handleShiftClick}
                        onCellClick={handleCellClick}
                      />
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <JobOfferPreviewDialog
        jobOffer={selectedJobOffer}
        open={!!selectedJobOffer}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedJobOffer(null);
          }
        }}
        onRequestEdit={
          onRequestEdit
            ? (jobOfferId) => {
                onRequestEdit(jobOfferId);
                setSelectedJobOffer(null);
              }
            : undefined
        }
        onCanceled={
          onCanceled
            ? () => {
                onCanceled();
                setSelectedJobOffer(null);
              }
            : undefined
        }
        user={user}
      />
    </>
  );
};
